import {
  useGetTourDetailCommissionReport,
  useGetTourDetailOtherExpenseReport,
  useGetTourDetailOtherIncomeReport,
  useGetTourDetailPurchaseReport,
  useGetTourDetailSaleReport,
  useGetTourDetailShowReport,
} from 'api-hooks/report';
import { RefreshSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Button } from 'components/elements/buttons';
import ListHeader from 'components/elements/list-header';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import useComposedQuery from 'hooks/use-composed-query';
import { Container } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { NavigationProps } from 'types';

import TourDetailCommissionCard from './commission-detail-card';
import ExportTourDetailButton from './export-excel';
import TourDetailOtherExpenseCard from './other-expense-detail-card';
import TourDetailOtherIncomeCard from './other-income-detail-card';
import TourDetailPurchaseCard from './purchase-detail-card';
import TourDetailSaleCard from './sale-detail-card';
import TourDetailShowCard from './show-detail-card';
import {
  ReportTourDetailCommissionColumn,
  ReportTourDetailOtherExpenseColumns,
  ReportTourDetailOtherIncomeColumns,
  ReportTourDetailPurchaseColumn,
  ReportTourDetailSaleColumn,
} from './table-columns';

export type ReportTourDetailParams = {
  id: string;
};

export default function ReportTourDetail(
  props: NavigationProps<NavigationRoutes.TourDetailReport>,
) {
  const { t } = useTranslation();
  const [pageSale, setPageSale] = React.useState(1);
  const [pagePurchase, setPagePurchase] = React.useState(1);
  const [pageCommission, setPageCommission] = React.useState(1);
  const [pageOtherIncome, setPageOtherIncome] = React.useState(1);
  const [pageOtherExpense, setPageOtherExpense] = React.useState(1);

  const { query } = useRouter();
  const tourRefId = query.id as string;

  const queryTourDetailShowReport = useGetTourDetailShowReport({
    tourRefId,
  });

  const {
    data: dataShow,
    refetch: refetchShow,
    isFetching: isFetchingShow,
    error: errorShow,
  } = queryTourDetailShowReport;

  const {
    data: dataSale,
    refetch: refetchSale,
    isFetching: isFetchingSale,
    error: errorSale,
    extras: [{ filters: filtersSale, setFilters: setFiltersSale }],
  } = useComposedQuery(
    useGetTourDetailSaleReport,
    {
      params: {
        tourRefId,
        page: pageSale,
      },
    },
    useApplyQueryFilter((data: any) => data.filters),
  );

  const {
    data: dataPurchase,
    refetch: refetchPurchase,
    isFetching: isFetchingPurchase,
    error: errorPurchase,
    extras: [{ filters: filtersPurchase, setFilters: setFiltersPurchase }],
  } = useComposedQuery(
    useGetTourDetailPurchaseReport,
    {
      params: {
        tourRefId,
        page: pagePurchase,
      },
    },
    useApplyQueryFilter((data: any) => data.filters),
  );

  const {
    data: dataCommission,
    refetch: refetchCommission,
    isFetching: isFetchingCommission,
    error: errorCommission,
    extras: [{ filters: filterCommission, setFilters: setFiltersCommission }],
  } = useComposedQuery(
    useGetTourDetailCommissionReport,
    {
      params: {
        tourRefId,
        page: pageCommission,
      },
    },
    useApplyQueryFilter((data: any) => data.filters),
  );

  const {
    data: dataOtherExpense,
    refetch: refetchOtherExpense,
    isFetching: isFetchingOtherExpense,
    error: errorOtherExpense,
    extras: [
      { filters: filterOtherExpense, setFilters: setFiltersOtherExpense },
    ],
  } = useComposedQuery(
    useGetTourDetailOtherExpenseReport,
    {
      params: {
        tourRefId,
        page: pageOtherExpense,
      },
    },
    useApplyQueryFilter((data: any) => data.filters),
  );

  const {
    data: dataOtherIncome,
    refetch: refetchOtherIncome,
    isFetching: isFetchingOtherIncome,
    error: errorOtherIncome,
    extras: [
      { filters: filtersOtherIncome, setFilters: setFiltersOtherIncome },
    ],
  } = useComposedQuery(
    useGetTourDetailOtherIncomeReport,
    {
      params: {
        tourRefId,
        page: pageOtherIncome,
      },
    },
    useApplyQueryFilter((data: any) => data.filters),
  );

  React.useEffect(() => {
    if (!!errorShow && !!errorShow.message) {
      notification.error({ message: errorShow?.message });
    }
  }, [errorShow]);

  React.useEffect(() => {
    if (!!errorSale && !!errorSale.message) {
      notification.error({ message: errorSale?.message });
    }
  }, [errorSale]);

  React.useEffect(() => {
    if (!!errorPurchase && !!errorPurchase.message) {
      notification.error({ message: errorPurchase?.message });
    }
  }, [errorPurchase]);

  React.useEffect(() => {
    if (!!errorCommission && !!errorCommission.message) {
      notification.error({ message: errorCommission?.message });
    }
  }, [errorCommission]);

  React.useEffect(() => {
    if (!!errorOtherExpense && !!errorOtherExpense.message) {
      notification.error({ message: errorOtherExpense?.message });
    }
  }, [errorOtherExpense]);

  React.useEffect(() => {
    if (!!errorOtherIncome && !!errorOtherIncome.message) {
      notification.error({ message: errorOtherIncome?.message });
    }
  }, [errorOtherIncome]);

  React.useEffect(() => {
    if (tourRefId) {
      refetchShow();
      refetchCommission();
      refetchPurchase();
      refetchSale();
      refetchOtherExpense();
      refetchOtherIncome();
    }
  }, [
    refetchCommission,
    refetchOtherExpense,
    refetchOtherIncome,
    refetchPurchase,
    refetchSale,
    refetchShow,
    tourRefId,
  ]);

  return (
    <Container>
      <ListHeader
        query={queryTourDetailShowReport}
        showAdd={false}
        title={t('common:tour_report_detail')}
        showRefresh={false}
        rightCustomComponent={
          <>
            {!!tourRefId && (
              <>
                <ExportTourDetailButton tourRefId={tourRefId} />
                <Separator gap={16} />
              </>
            )}
            <Button
              onClick={() => {
                refetchShow();
                refetchCommission();
                refetchPurchase();
                refetchSale();
                refetchOtherExpense();
                refetchOtherIncome();
              }}
              startEnhancer={(color, size) => (
                <RefreshSVG color={color} size={size} />
              )}
              buttonType="secondary"
              style={styles.button}
            >
              {t('common:refresh')}
            </Button>
          </>
        }
      />
      <TourDetailShowCard data={dataShow?.data} isLoading={isFetchingShow} />
      <Separator gap={16} direction="vertical" />
      <TourDetailSaleCard
        data={dataSale?.data}
        isLoading={isFetchingSale}
        filters={filtersSale}
        onPageChange={setPageSale}
        columns={ReportTourDetailSaleColumn({ t })}
        page={pageSale}
        setFilters={setFiltersSale}
      />
      <Separator gap={16} direction="vertical" />
      <TourDetailPurchaseCard
        data={dataPurchase?.data}
        isLoading={isFetchingPurchase}
        filters={filtersPurchase}
        onPageChange={setPagePurchase}
        columns={ReportTourDetailPurchaseColumn({ t })}
        page={pagePurchase}
        setFilters={setFiltersPurchase}
      />
      <Separator gap={16} direction="vertical" />
      <TourDetailOtherIncomeCard
        data={dataOtherIncome?.data}
        isLoading={isFetchingOtherIncome}
        onPageChange={setPageOtherIncome}
        filters={filtersOtherIncome}
        setFilters={setFiltersOtherIncome}
        columns={ReportTourDetailOtherIncomeColumns({ t })}
        page={pageOtherIncome}
      />
      <Separator gap={16} direction="vertical" />
      <TourDetailOtherExpenseCard
        data={dataOtherExpense?.data}
        isLoading={isFetchingOtherExpense}
        onPageChange={setPageOtherExpense}
        filters={filterOtherExpense}
        setFilters={setFiltersOtherExpense}
        columns={ReportTourDetailOtherExpenseColumns({ t })}
        page={pageOtherExpense}
      />
      <Separator gap={16} direction="vertical" />
      <TourDetailCommissionCard
        data={dataCommission?.data}
        isLoading={isFetchingCommission}
        filters={filterCommission}
        setFilters={setFiltersCommission}
        onPageChange={setPageCommission}
        columns={ReportTourDetailCommissionColumn({ t })}
        page={pageCommission}
      />
      <Separator gap={16} direction="vertical" />
    </Container>
  );
}

const styles = {
  button: {
    minWidth: '120px',
  },
};
